import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { FiDownload, FiRefreshCw } from 'react-icons/fi';
import { AuthContext } from '../context/AuthContext';
import { ThemeContext } from '../context/ThemeContext';
import { useApi } from '../hooks/useApi';
import { FormAlert } from '../components/form';
import PillGroup from '../components/PillGroup';
import { canAccessTeamAudit } from '../utils/auditAccess';
import {
  ACTIVITY_FILTERS,
  activityMeta,
  formatActivityDate,
  timeAgo,
} from '../utils/activityFormat';

const PAGE_SIZE = 25;

function normalizeActivity(row) {
  return {
    id: row.id || row._id || `${row.createdAt}-${row.action}`,
    action: row.action || 'Actividad',
    details: row.details || '',
    createdAt: row.createdAt,
    user: row.user || null,
  };
}

function isToday(dateString) {
  const d = new Date(dateString);
  const now = new Date();
  return d.getFullYear() === now.getFullYear()
    && d.getMonth() === now.getMonth()
    && d.getDate() === now.getDate();
}

function isThisWeek(dateString) {
  const d = new Date(dateString);
  const now = new Date();
  const weekAgo = new Date(now);
  weekAgo.setDate(now.getDate() - 7);
  return d >= weekAgo;
}

const Auditoria = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { modoOscuro } = useContext(ThemeContext);
  const { secureFetch } = useApi();

  const teamAccess = canAccessTeamAudit(user);
  const [scope, setScope] = useState(teamAccess ? 'equipo' : 'personal');
  const [company, setCompany] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('todos');
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  const box = modoOscuro ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200';
  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textSec = modoOscuro ? 'text-gray-300' : 'text-gray-600';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';
  const inputBg = modoOscuro
    ? 'bg-gray-800 border-gray-700 text-white placeholder:text-gray-500'
    : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-400';

  const toneClass = (tone) => {
    const map = {
      blue: modoOscuro ? 'bg-[#1F80FF]/20 text-[#5CA3FF]' : 'bg-[#1F80FF]/10 text-[#1F80FF]',
      indigo: modoOscuro ? 'bg-indigo-900/40 text-indigo-200' : 'bg-indigo-50 text-indigo-700',
      green: modoOscuro ? 'bg-green-900/40 text-green-200' : 'bg-green-50 text-green-700',
      amber: modoOscuro ? 'bg-amber-900/40 text-amber-200' : 'bg-amber-50 text-amber-700',
      red: modoOscuro ? 'bg-red-900/40 text-red-200' : 'bg-red-50 text-red-700',
      gray: modoOscuro ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700',
    };
    return map[tone] || map.gray;
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const useTeam = teamAccess && scope === 'equipo';
      const res = await secureFetch(useTeam ? '/api/team/activity' : '/api/activity');
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'No se pudo cargar la auditoría');
      }
      const data = await res.json();
      if (useTeam) {
        setCompany(data.company || null);
        setActivities((data.activities || []).map(normalizeActivity));
      } else {
        setCompany(null);
        const rows = Array.isArray(data) ? data : [];
        setActivities(rows.map(normalizeActivity));
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error al cargar la auditoría.');
      setActivities([]);
    } finally {
      setLoading(false);
    }
  }, [secureFetch, scope, teamAccess]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, filter, scope, activities.length]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return activities.filter((a) => {
      const meta = activityMeta(a.action);
      if (filter !== 'todos' && meta.category !== filter) return false;
      if (!q) return true;
      return (
        (a.action || '').toLowerCase().includes(q)
        || (a.details || '').toLowerCase().includes(q)
        || (a.user?.name || '').toLowerCase().includes(q)
        || (a.user?.email || '').toLowerCase().includes(q)
      );
    });
  }, [activities, searchTerm, filter]);

  const stats = useMemo(() => {
    const users = new Set(filtered.map((a) => a.user?.email || a.user?.name).filter(Boolean));
    return {
      total: filtered.length,
      hoy: filtered.filter((a) => isToday(a.createdAt)).length,
      semana: filtered.filter((a) => isThisWeek(a.createdAt)).length,
      usuarios: users.size,
    };
  }, [filtered]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filtered, page]
  );

  const handleExport = async () => {
    if (scope !== 'equipo' || !teamAccess) return;
    setExporting(true);
    setError('');
    try {
      const res = await secureFetch('/api/team/activity/export');
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'No se pudo exportar la auditoría');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `auditoria-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Error al exportar.');
    } finally {
      setExporting(false);
    }
  };

  const scopeOptions = teamAccess
    ? [
        { id: 'equipo', label: 'Equipo' },
        { id: 'personal', label: 'Mi actividad' },
      ]
    : [];

  return (
    <div className={modoOscuro ? 'w-full bg-black text-white' : 'w-full bg-[#f5f5f7] text-gray-900'}>
      <div className="w-full max-w-[85rem] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-12 py-4 sm:py-5 lg:py-6 pb-12 sm:pb-16 space-y-4 sm:space-y-5">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <button
              type="button"
              onClick={() => navigate('/inicio')}
              className={`p-2 rounded-lg border shrink-0 ${modoOscuro ? 'border-gray-700 bg-gray-900 hover:bg-gray-800' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
              aria-label="Volver al inicio"
            >
              <ArrowLeftIcon className="w-4 h-4" />
            </button>
            <div className="min-w-0">
              <h1 className={`text-xl sm:text-2xl lg:text-3xl font-semibold leading-tight ${textPri}`}>
                Auditoría
              </h1>
              <p className={`text-sm mt-1 ${textSec}`}>
                {scope === 'equipo' && company?.name
                  ? `Historial del equipo de ${company.name}`
                  : 'Historial de cambios y acciones en tu cuenta'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {teamAccess && scope === 'equipo' && (
              <button
                type="button"
                onClick={handleExport}
                disabled={exporting || loading}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-medium ${
                  modoOscuro ? 'border-gray-600 hover:bg-gray-800' : 'border-gray-200 bg-white hover:bg-gray-50'
                } disabled:opacity-50`}
              >
                <FiDownload className="w-4 h-4" />
                {exporting ? 'Exportando…' : 'Exportar CSV'}
              </button>
            )}
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-medium ${
                modoOscuro ? 'border-gray-600 hover:bg-gray-800' : 'border-gray-200 bg-white hover:bg-gray-50'
              } disabled:opacity-50`}
            >
              <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Actualizar
            </button>
          </div>
        </header>

        {teamAccess && (
          <div className="flex flex-wrap items-center gap-3">
            <PillGroup value={scope} onChange={setScope} options={scopeOptions} modoOscuro={modoOscuro} />
            <Link
              to="/equipo/usuarios"
              className={`text-xs font-medium text-[#1F80FF] hover:underline`}
            >
              Gestionar usuarios del equipo
            </Link>
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3">
          {[
            { label: 'Registros', value: loading ? '—' : String(stats.total), hint: 'en el filtro actual' },
            { label: 'Hoy', value: loading ? '—' : String(stats.hoy), hint: 'acciones de hoy' },
            { label: 'Esta semana', value: loading ? '—' : String(stats.semana), hint: 'últimos 7 días' },
            ...(scope === 'equipo'
              ? [{ label: 'Usuarios', value: loading ? '—' : String(stats.usuarios), hint: 'con actividad' }]
              : [{ label: 'Alcance', value: 'Vos', hint: 'solo tu cuenta' }]),
          ].map((card) => (
            <div key={card.label} className={`rounded-2xl border p-3.5 sm:p-4 ${box}`}>
              <div className={`text-[11px] font-semibold tracking-wide uppercase ${textMuted}`}>{card.label}</div>
              <div className={`text-lg sm:text-xl font-semibold mt-0.5 ${textPri}`}>{card.value}</div>
              <div className={`text-[11px] mt-0.5 ${textMuted}`}>{card.hint}</div>
            </div>
          ))}
        </div>

        <section className={`rounded-2xl border overflow-hidden ${box}`}>
          <div className={`p-4 sm:p-5 border-b space-y-3 ${modoOscuro ? 'border-gray-700/80' : 'border-gray-200'}`}>
            <PillGroup value={filter} onChange={setFilter} options={ACTIVITY_FILTERS} modoOscuro={modoOscuro} />
            <div className="relative">
              <MagnifyingGlassIcon className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${textMuted}`} />
              <input
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por acción, detalle o usuario..."
                className={`w-full pl-9 pr-3 py-2 rounded-lg border text-sm ${inputBg} focus:outline-none focus:ring-2 focus:ring-[#1F80FF]/30`}
              />
            </div>
          </div>

          <FormAlert type="error" className="mx-4 mt-4">{error}</FormAlert>

          {loading ? (
            <div className="p-4 sm:p-5 space-y-2">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className={`h-20 rounded-xl animate-pulse ${modoOscuro ? 'bg-gray-800' : 'bg-gray-100'}`} />
              ))}
            </div>
          ) : paginated.length === 0 ? (
            <div className="px-4 py-14 text-center">
              <p className={`text-sm font-medium ${textPri}`}>No hay actividad en este recorte</p>
              <p className={`text-xs mt-1 ${textMuted}`}>
                {searchTerm || filter !== 'todos'
                  ? 'Probá otro filtro o término de búsqueda.'
                  : 'Cuando registres ventas, clientes o cobros, van a aparecer acá.'}
              </p>
            </div>
          ) : (
            <div className={`divide-y ${modoOscuro ? 'divide-gray-800' : 'divide-gray-100'}`}>
              {paginated.map((activity) => {
                const meta = activityMeta(activity.action);
                const Icon = meta.Icon;
                return (
                  <div
                    key={activity.id}
                    className={`px-4 sm:px-5 py-4 flex gap-3 sm:gap-4 ${
                      modoOscuro ? 'hover:bg-gray-800/50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${toneClass(meta.tone)}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className={`text-sm font-semibold ${textPri}`}>{activity.action}</h3>
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${toneClass(meta.tone)}`}>
                              {meta.label}
                            </span>
                          </div>
                          {activity.user?.name && (
                            <p className={`text-xs mt-0.5 ${textMuted}`}>
                              {activity.user.name}
                              {activity.user.email ? ` · ${activity.user.email}` : ''}
                            </p>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <div className={`text-xs font-medium ${textSec}`}>{timeAgo(activity.createdAt)}</div>
                          <div className={`text-[10px] ${textMuted}`}>{formatActivityDate(activity.createdAt)}</div>
                        </div>
                      </div>
                      {activity.details && (
                        <p className={`text-sm mt-2 leading-relaxed ${textSec}`}>{activity.details}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!loading && filtered.length > PAGE_SIZE && (
            <div className={`px-4 sm:px-5 py-3 border-t flex items-center justify-between text-[11px] ${modoOscuro ? 'border-gray-800' : 'border-gray-100'}`}>
              <span className={textMuted}>
                {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} de {filtered.length}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`px-2.5 py-1 rounded-lg border disabled:opacity-40 ${
                    modoOscuro ? 'border-gray-700 hover:bg-gray-800' : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  Anterior
                </button>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className={`px-2.5 py-1 rounded-lg border disabled:opacity-40 ${
                    modoOscuro ? 'border-gray-700 hover:bg-gray-800' : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  Siguiente
                </button>
              </div>
            </div>
          )}
        </section>

        <p className={`text-xs ${textMuted}`}>
          Registro interno de acciones. No reemplaza logs oficiales ni auditoría contable externa.
        </p>
      </div>
    </div>
  );
};

export default Auditoria;
